import { filterObject } from '@/common/utilities';
import type { InitializeOptions } from '@/common/types';

/**
 * Flattened, secret-free view of the legacy bridge `INIT` payload.
 *
 * Loki metadata is flat and size-capped (nested values are dropped once a log entry grows
 * past ~63KB), so the payload is projected into primitives instead of being dumped as-is.
 * Bulk fields (`customisation`) are reduced to counts and secrets to masked previews.
 */
export interface InitEventTelemetry {
  event: 'widget_init';
  /** Top-level keys the host actually sent, so new/unexpected fields show up without logging values. */
  payload_keys: string;

  platform?: string;
  app_version?: string;
  app_name?: string;
  pleased_url?: string;
  brand_code?: string;
  client_id?: string;
  widget_id?: string;
  country?: string;
  direct_link?: boolean;
  open?: boolean;
  page_source?: string;

  default_language?: string;
  enabled_languages?: string;

  login_method?: string;
  login_username_type?: string;
  hide_login_button?: boolean;
  support_jwe_token?: boolean;
  support_voice?: boolean;

  /** Deprecated top-level `token` — masked, never logged in full. */
  token?: string;
  token_length?: number;
  sporty_token?: string;
  sporty_token_length?: number;
  sporty_country?: string;
  sporty_currency?: string;
  sporty_user_country?: string;

  host_user_id?: string;
  email?: string;
  phone?: string;
  has_name?: boolean;

  meta_tags_keys?: string;
  /** `TransactionType` is a numeric enum (1 deposit / 2 withdrawal / 3 bet). */
  selected_entity_type?: number;
  support_twilio_voice_call?: boolean;
  failed_kyc_show?: boolean;

  customisation_count?: number;
  initial_registry_id?: string;

  header_show?: boolean;
  header_show_login?: boolean;
  header_show_dots?: boolean;
  bubble_show?: boolean;

  firebase_project_id?: string;
  firebase_database?: string;
}

const MASK = '***';

/** Below this length a token is short enough that first/last characters would give it away. */
const MIN_MASKABLE_SECRET_LENGTH = 12;

/**
 * Keep enough of a secret to correlate two log lines, never enough to replay it:
 * `patron:id:accesstoken:abc…9f2z`. Anything short is masked outright.
 */
export const maskSecret = (value?: string): string | undefined => {
  if (!value) return undefined;
  if (value.length < MIN_MASKABLE_SECRET_LENGTH) return MASK;
  return `${value.slice(0, 4)}${MASK}${value.slice(-4)}`;
};

/**
 * `john.doe@example.com` → `j***@example.com`; keeps the domain, which is the useful part.
 * Anything without both a local part and a domain is not email-shaped and is masked outright.
 */
export const maskEmail = (value?: string): string | undefined => {
  if (!value) return undefined;
  const atIndex = value.lastIndexOf('@');
  if (atIndex < 1 || atIndex === value.length - 1) return MASK;
  return `${value[0]}${MASK}${value.slice(atIndex)}`;
};

/** Keeps the last 4 digits so a report can be matched against a ticket. */
export const maskPhone = (value?: string): string | undefined => {
  if (!value) return undefined;
  if (value.length <= 4) return MASK;
  return `${MASK}${value.slice(-4)}`;
};

/**
 * Project the host `INIT` payload into flat Loki metadata with secrets and PII masked.
 *
 * Absent and blank fields are filtered out so they cannot clobber same-named keys from the
 * shared log metadata (e.g. `brand_code`) when spread over it, and so the metadata cannot
 * disagree with the log message. Pure — see `logWidgetInitEvent` for the push.
 */
export const buildInitEventTelemetry = (payload: InitializeOptions): InitEventTelemetry => {
  const token = maskSecret(payload.token);
  const sportyToken = maskSecret(payload.sporty?.token);
  const metaTagsKeys = payload.metaTags ? Object.keys(payload.metaTags) : undefined;

  return filterObject<InitEventTelemetry>(
    {
      event: 'widget_init',
      payload_keys: Object.keys(payload).sort().join(','),

      platform: payload.platform,
      app_version: payload.appVersion,
      app_name: payload.appName,
      pleased_url: payload.pleasedUrl,
      brand_code: payload.brandCode,
      client_id: payload.clientId,
      widget_id: payload.widgetId,
      country: payload.country,
      direct_link: payload.directLink,
      open: payload.open,
      page_source: payload.pageSource,

      default_language: payload.defaultLanguage,
      enabled_languages: payload.enabledLanguages?.join(','),

      login_method: payload.loginMethod,
      login_username_type: payload.login?.usernameType,
      hide_login_button: payload.hideLoginButton,
      support_jwe_token: payload.supportJweToken,
      support_voice: payload.supportVoice,

      token,
      token_length: payload.token?.length,
      sporty_token: sportyToken,
      sporty_token_length: payload.sporty?.token?.length,
      sporty_country: payload.sporty?.country,
      sporty_currency: payload.sporty?.currency,
      sporty_user_country: payload.sporty?.userCountry,

      host_user_id: payload.userId,
      email: maskEmail(payload.email),
      phone: maskPhone(payload.phone),
      has_name: Boolean(payload.name),

      meta_tags_keys: metaTagsKeys?.length ? metaTagsKeys.sort().join(',') : undefined,
      selected_entity_type: payload.selectedEntity?.type,
      support_twilio_voice_call: payload.capabilities?.supportTwilioVoiceCall,
      failed_kyc_show: payload.failedKyc?.show,

      customisation_count: payload.customisation?.length,
      initial_registry_id: payload.initialRegistryId,

      header_show: payload.uiConfig?.header?.show,
      header_show_login: payload.uiConfig?.header?.showLogin,
      header_show_dots: payload.uiConfig?.header?.showDots,
      bubble_show: payload.uiConfig?.bubble?.show,

      // `firebaseConfig.auth` is an API key and is deliberately never logged.
      firebase_project_id: payload.firebaseConfig?.projectId,
      firebase_database: payload.firebaseConfig?.database,
    },
    (value) => value != null && value !== ''
  );
};
